/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useMemo } from 'react';

// Component imports
import { Grid } from 'antd';
import { addOpacityToColor } from '../../function/addOpacityToColor';

// Constant declarations
const { useBreakpoint } = Grid;

/**
 * Retrieves the theme values from the CSS variables defined in the document's root element.
 * @returns {Object} An object containing the theme values.
 */
function useTheme() {
  const screens = useBreakpoint();
  const isMobileSize = (screens.xs || screens.sm) && !screens.lg && !screens.md;
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  // Layout
  const headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--layout-header-height').trim();
  const footerHeight = getComputedStyle(document.documentElement).getPropertyValue('--layout-footer-height').trim();

  // Colors
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim();
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color').trim();
  const textColor = getComputedStyle(document.documentElement).getPropertyValue('--text-color').trim();
  const textColorSecondary = getComputedStyle(document.documentElement).getPropertyValue('--text-color-secondary').trim();
  const successColor = getComputedStyle(document.documentElement).getPropertyValue('--success-color').trim();
  const warningColor = getComputedStyle(document.documentElement).getPropertyValue('--warning-color').trim();
  const errorColor = getComputedStyle(document.documentElement).getPropertyValue('--error-color').trim();
  const successColorAnalytics = addOpacityToColor(successColor, 0.7);
  const warningColorAnalytics = addOpacityToColor(warningColor, 0.7);
  const errorColorAnalytics = addOpacityToColor(errorColor, 0.7);
  const primaryColorAnalytics = addOpacityToColor(primaryColor, 0.85);
  const primaryColorAnalyticsOpacity = addOpacityToColor(primaryColor, 0.6);

  // Paddings
  const paddingXss = getComputedStyle(document.documentElement).getPropertyValue('--padding-xss').trim();
  const paddingXs = getComputedStyle(document.documentElement).getPropertyValue('--padding-xs').trim();
  const paddingSm = getComputedStyle(document.documentElement).getPropertyValue('--padding-sm').trim();
  const paddingMd = getComputedStyle(document.documentElement).getPropertyValue('--padding-md').trim();
  const paddingLg = getComputedStyle(document.documentElement).getPropertyValue('--padding-lg').trim();

  // Fonts
  const fontSizeXs = getComputedStyle(document.documentElement).getPropertyValue('--font-size-xs').trim();
  const fontSizeSm = getComputedStyle(document.documentElement).getPropertyValue('--font-size-sm').trim();
  const fontSizeBase = getComputedStyle(document.documentElement).getPropertyValue('--font-size-base').trim();
  const fontSizeLg = getComputedStyle(document.documentElement).getPropertyValue('--font-size-lg').trim();
  const fontSizeXl = getComputedStyle(document.documentElement).getPropertyValue('--font-size-xl').trim();
  const fontSizeXxl = getComputedStyle(document.documentElement).getPropertyValue('--font-size-xxl').trim();
  const fontSizeXxxl = getComputedStyle(document.documentElement).getPropertyValue('--font-size-xxxl').trim();
  const fontSizeXxxxl = getComputedStyle(document.documentElement).getPropertyValue('--font-size-xxxxl').trim();

  // Borders
  const borderColorBase = getComputedStyle(document.documentElement).getPropertyValue('--border-color-base').trim();
  const borderRadiusBase = getComputedStyle(document.documentElement).getPropertyValue('--border-radius-base').trim();

  // Graphs
  const messageNodeIconColor = '#a49a02';
  const jumpNodeIconColor = '#748cc3';
  const defaultNodeIconColor = getComputedStyle(document.documentElement).getPropertyValue('--text-color').trim();

  // Misc
  const livechatPanelHeight = `calc(100vh - ${headerHeight} - ${footerHeight} - 12px - 54px - 36px - 16px)`; // - layout padding - page title - tabs height - tabs margin
  const hrwizPanelHeight = `calc(100vh - ${headerHeight} - ${footerHeight} - 12px - 24px - 24px)`; // - layout padding - divider - padding
  const botFormPanelHeight = `calc(100vh - ${headerHeight} - ${footerHeight} - 8px - 54px - 8px)`; // - layout padding - page title - padding

  const formProps = useMemo(() => {
    return {
      flexDirection: `${isMobileSize ? 'flex-column' : 'flex-row'}`,
      mainColSpan: isMobileSize ? 24 : 6,
      secondaryColSpan: isMobileSize ? 24 : 18,
      descriptionStyle: { maxWidth: isMobileSize ? '100%' : '180px' },
      rowGutter: isMobileSize ? [8, 8] : [24, 24],
      warningDescription: isMobileSize ? 22 : 21,
      warningIconMobileDistance: isMobileSize ? 1 : 2,
      languagesPopoverWidth: isMobileSize ? 'min(80vw, 300px)' : 'min(34vw, 600px)',
      maxInputWidth: isMobileSize ? '100%' : '480px'
    };
  }, [isMobileSize]);

  return {
    // Layout
    headerHeight,
    footerHeight,

    // Colors
    primaryColor,
    secondaryColor,
    textColor,
    textColorSecondary,
    successColor,
    successColorAnalytics,
    warningColor,
    warningColorAnalytics,
    errorColor,
    errorColorAnalytics,
    primaryColorAnalytics,
    primaryColorAnalyticsOpacity,

    // Paddings
    paddingXss,
    paddingXs,
    paddingSm,
    paddingMd,
    paddingLg,

    // Fonts
    fontSizeXs,
    fontSizeSm,
    fontSizeBase,
    fontSizeLg,
    fontSizeXl,
    fontSizeXxl,
    fontSizeXxxl,
    fontSizeXxxxl,

    // Borders
    borderColorBase,
    borderRadiusBase,

    // Graphs
    messageNodeIconColor,
    jumpNodeIconColor,
    defaultNodeIconColor,

    // Misc
    livechatPanelHeight,
    hrwizPanelHeight,
    botFormPanelHeight,
    isMobileSize,
    isMobileOrTabletSize,

    // Form
    formProps
  };
}

export default useTheme;
